import { useState } from "react";
import { ArrowDown, ArrowUp, RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { StockProduct } from "@/hooks/useProductStock";
import { ExpenseLinkFields, type ExpenseLinkDraft } from "@/components/expenses/ExpenseLinkFields";

type MovementType = "entry" | "exit" | "adjustment";

const movementTypes = [
  {
    value: "entry" as const,
    label: "Entrada",
    description: "Adicionar unidades ao estoque",
    icon: ArrowDown,
    color: "text-green-600 bg-green-100",
  },
  {
    value: "exit" as const,
    label: "Saída",
    description: "Remover unidades do estoque",
    icon: ArrowUp,
    color: "text-red-600 bg-red-100",
  },
  {
    value: "adjustment" as const,
    label: "Ajuste",
    description: "Definir quantidade exata",
    icon: RefreshCw,
    color: "text-blue-600 bg-blue-100",
  },
];

interface ProductStockMovementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: StockProduct | null;
  onSave: (
    type: MovementType,
    quantity: number,
    reason?: string,
    cost?: number,
    expense?: {
      title: string;
      category_id: string;
      supplier_id?: string;
      due_date?: string;
    }
  ) => Promise<void>;
}

export function ProductStockMovementDialog({
  open,
  onOpenChange,
  product,
  onSave,
}: ProductStockMovementDialogProps) {
  const [movementType, setMovementType] = useState<MovementType>("entry");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [cost, setCost] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [expenseDraft, setExpenseDraft] = useState<ExpenseLinkDraft>({
    enabled: false,
    title: "",
    categoryId: "",
    supplierId: undefined,
    dueDate: undefined,
  });

  const handleSubmit = async () => {
    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) return;

    const costValue = cost ? parseFloat(cost) : undefined;
    const showExpenseLink = movementType === "entry" && !!costValue && costValue > 0;
    const shouldLinkExpense = showExpenseLink && expenseDraft.enabled;
    const canSubmitExpense =
      !shouldLinkExpense ||
      (expenseDraft.title.trim().length > 0 && !!expenseDraft.categoryId);

    if (!canSubmitExpense) return;

    setIsSubmitting(true);
    try {
      await onSave(
        movementType,
        qty,
        reason || undefined,
        costValue,
        shouldLinkExpense
          ? {
              title: expenseDraft.title.trim(),
              category_id: expenseDraft.categoryId,
              supplier_id: expenseDraft.supplierId,
              due_date: expenseDraft.dueDate,
            }
          : undefined
      );
      // Reset form
      setMovementType("entry");
      setQuantity("");
      setReason("");
      setCost("");
      setExpenseDraft({ enabled: false, title: "", categoryId: "", supplierId: undefined, dueDate: undefined });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getNewStock = () => {
    if (!product) return 0;
    const qty = parseFloat(quantity) || 0;
    if (movementType === "entry") return product.current_stock + qty;
    if (movementType === "exit") return Math.max(0, product.current_stock - qty);
    return qty;
  };

  if (!product) return null;

  const costValue = cost ? parseFloat(cost) : 0;
  const showExpenseLink = movementType === "entry" && costValue > 0;
  const canSubmitExpense =
    !showExpenseLink ||
    !expenseDraft.enabled ||
    (expenseDraft.title.trim().length > 0 && !!expenseDraft.categoryId);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Movimentação de Estoque</DialogTitle>
          <DialogDescription>
            <span className="font-medium text-foreground">{product.name}</span>
            <span className="text-muted-foreground">
              {" "}— Estoque atual: {product.current_stock} un
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Movement Type Selection */}
          <div className="grid grid-cols-3 gap-2">
            {movementTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setMovementType(type.value)}
                className={cn(
                  "flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all",
                  movementType === type.value
                    ? "border-primary bg-primary/5"
                    : "border-transparent bg-muted/50 hover:bg-muted"
                )}
              >
                <div className={cn("p-2 rounded-full", type.color)}>
                  <type.icon className="h-4 w-4" />
                </div>
                <span className="text-sm font-medium">{type.label}</span>
              </button>
            ))}
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label htmlFor="quantity">
              {movementType === "adjustment" ? "Nova Quantidade" : "Quantidade"}
            </Label>
            <Input
              id="quantity"
              type="number"
              min="0"
              step="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="0"
            />
          </div>

          {/* Cost (only for entry) */}
          {movementType === "entry" && (
            <div className="space-y-2">
              <Label htmlFor="cost">Custo Total da Compra (opcional)</Label>
              <Input
                id="cost"
                type="number"
                min="0"
                step="0.01"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder={formatCurrency(product.cost_per_unit)}
              />
              <p className="text-xs text-muted-foreground">
                Deixe vazio para manter o custo atual
              </p>
            </div>
          )}

          {showExpenseLink && (
            <div className="rounded-lg border p-3">
              <ExpenseLinkFields
                value={expenseDraft}
                onChange={(next) => {
                  if (!expenseDraft.enabled && next.enabled && !next.title) {
                    setExpenseDraft({
                      ...next,
                      title: reason?.trim()
                        ? `Compra estoque - ${product.name} (${reason.trim()})`
                        : `Compra estoque - ${product.name}`,
                    });
                    return;
                  }
                  setExpenseDraft(next);
                }}
                checkboxLabel="Gerar conta a pagar (DESPESA)"
                titleLabel="Título da despesa"
              />
              {expenseDraft.enabled && !canSubmitExpense && (
                <p className="mt-2 text-xs text-muted-foreground">
                  Informe ao menos o título e a categoria.
                </p>
              )}
            </div>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Motivo (opcional)</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Compra do fornecedor, Quebra, Inventário..."
              rows={2}
            />
          </div>

          {/* Preview */}
          {quantity && parseFloat(quantity) > 0 && (
            <div className="p-3 rounded-lg bg-muted/50 text-sm">
              <span className="text-muted-foreground">Novo estoque: </span>
              <span className="font-medium">{getNewStock()} un</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              isSubmitting || !quantity || parseFloat(quantity) <= 0 || !canSubmitExpense
            }
          >
            {isSubmitting ? "Salvando..." : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
